import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { ModuleKey } from './moduleCatalog';

// Settings → Module (backend/src/routes/modules.ts): which product modules are
// on for the whole instance. GET is open to every signed-in user, PUT is
// ADMIN-only. A module the Firmio Console did not grant stays off whatever
// the admin saves.

interface Envelope<T> {
  success: true;
  data: T;
}

export interface ModuleState {
  key: ModuleKey;
  parent: ModuleKey | null;
  /** Core modules cannot be switched off. */
  core: boolean;
  /** Effective: granted by the console, not switched off here, parent on. */
  enabled: boolean;
  /**
   * False when the customer's subscription (Firmio Console) does not include
   * it. For an opt-in module: whether it was granted.
   */
  allowedByConsole: boolean;
  /** Built for one customer: off until granted (console, or here when unlinked). */
  optIn?: boolean;
  disabledLocally: boolean;
}

export interface ModulesPayload {
  /** The instance reports to a Firmio Console (which may set a ceiling). */
  consoleLinked: boolean;
  modules: ModuleState[];
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function getModules(): Promise<ModulesPayload> {
  const res = await apiFetch<Envelope<ModulesPayload>>('/api/modules', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function saveDisabledModules(
  disabled: ModuleKey[],
  /** Opt-in grants; only accepted on an instance not linked to a console. */
  optIn?: ModuleKey[],
): Promise<ModulesPayload> {
  const res = await apiFetch<Envelope<ModulesPayload>>('/api/modules', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(optIn ? { disabled, optIn } : { disabled }),
  });
  return res.data;
}
